import {
	ApplicationError,
	BackendErrors,
	type Collaborator,
	ErrorType,
	ForManagingProjects,
	type News,
	type Project,
	type ProjectDetails,
	ProjectV2
} from 'core'
import { z } from 'zod'

import { GuardInput } from '../utils/guard'

export class ProjectService implements ForManagingProjects {
	private readonly _projectManager: ForManagingProjects
	private readonly _defaultErrorCode = 'project.unknown'

	constructor(projectManager: ForManagingProjects) {
		this._projectManager = projectManager
	}

	static ValidateProjectName(
		name: string,
		projects: Project[]
	): ApplicationError | void {
		if (!name) return
		// TODO: Need to validate the project owner and the project name
		// backend have to implement the logic to return the owner of the project
		const isNameAlreadyInUse = projects.find(project => project.name === name)
		if (isNameAlreadyInUse) {
			return new ApplicationError({
				type: ErrorType.InvalidInput,
				message: 'The project name is already in use',
				inputErrors: {
					name: 'The project name is already in use'
				},
				code: 'project_name_in_use'
			})
		}
		return GuardInput.check(ForManagingProjects.CreateProjectPayload, { name })
	}

	static ValidateSmartContractName(name: string) {
		return GuardInput.check(ForManagingProjects.CreateSmartContract, {
			smartContract: { name }
		})
	}

	static ValidateDAppName(name: string) {
		return GuardInput.check(ForManagingProjects.CreateSmartContract, {
			project: { name }
		})
	}

	async getProjectsAndNews(): Promise<{
		data?: {
			news: News[]
			projects: ProjectV2[]
		}
		error?: ApplicationError
	}> {
		const { data, error } = await this._projectManager.getProjectsAndNews()

		if (error) {
			return { error: BackendErrors.check(error, this._defaultErrorCode) }
		}

		data!.projects = data!.projects.map(ProjectService.AssignColorToProject)

		return { data }
	}

	getUserProjects(): Promise<{
		data?: {
			news: News[]
			projects: Project[]
		}
		error?: ApplicationError
	}> {
		return this._projectManager.getUserProjects()
	}

	async getUserProjectDetails(projectId: string): Promise<{
		data?: ProjectDetails
		error?: ApplicationError
	}> {
		const projectIdHasError = GuardInput.check(
			ForManagingProjects.CreateProjectPayload.partial(),
			{ projectId }
		)

		if (projectIdHasError) return { error: projectIdHasError }

		const { data, error } =
			await this._projectManager.getUserProjectDetails(projectId)

		if (error) {
			return { error: BackendErrors.check(error, this._defaultErrorCode) }
		}

		return { data }
	}

	async createProject(
		payload: z.infer<typeof ForManagingProjects.CreateProjectPayload>
	): Promise<{
		data?: Project
		error?: ApplicationError
	}> {
		const payloadError = GuardInput.check(
			ForManagingProjects.CreateProjectPayload,
			payload
		)

		if (payloadError) return { error: payloadError }

		const { data, error } = await this._projectManager.createProject(payload)

		if (error) {
			return { error: BackendErrors.check(error, this._defaultErrorCode) }
		}

		return { data }
	}

	async createSmartContract(
		payload: z.infer<typeof ForManagingProjects.CreateSmartContract>
	): Promise<{
		data?: ProjectV2
		error?: ApplicationError
	}> {
		const payloadError = GuardInput.check(
			ForManagingProjects.CreateSmartContract,
			payload
		)

		if (payloadError) return { error: payloadError }

		const { data, error } =
			await this._projectManager.createSmartContract(payload)

		if (error) {
			return { error: BackendErrors.check(error, this._defaultErrorCode) }
		}

		return { data: ProjectService.AssignColorToProject(data!) }
	}

	async addCollaborator(
		payload: z.infer<typeof ForManagingProjects.AddCollaboratorPayload>
	): Promise<{
		data?: Collaborator
		error?: ApplicationError
	}> {
		const payloadError = GuardInput.check(
			ForManagingProjects.AddCollaboratorPayload,
			payload
		)

		if (payloadError) return { error: payloadError }

		const { data, error } = await this._projectManager.addCollaborator(payload)

		if (error) {
			return { error: BackendErrors.check(error, this._defaultErrorCode) }
		}

		return {
			data
		}
	}

	async removeCollaborator(
		payload: z.infer<typeof ForManagingProjects.RemoveCollaboratorPayload>
	) {
		const { error } = await this._projectManager.removeCollaborator(payload)

		if (error) {
			return { error: BackendErrors.check(error, this._defaultErrorCode) }
		}

		return {}
	}

	async acceptCollaboratorInvitation(
		payload: z.infer<typeof ForManagingProjects.AcceptCollaboratorInvitation>
	): Promise<{
		error?: ApplicationError
		data?: ProjectV2
	}> {
		const { error, data } =
			await this._projectManager.acceptCollaboratorInvitation(payload)

		if (error) {
			return { error: BackendErrors.check(error, this._defaultErrorCode) }
		}

		return { data: ProjectService.AssignColorToProject(data!) }
	}

	static ValidateCollaboratorName(
		name: string,
		currentUser?: string,
		collaborators?: Collaborator[]
	): ApplicationError | void {
		const isCollaborator = collaborators?.some(
			collaborator => collaborator.userHandler === name
		)

		if (isCollaborator) {
			return new ApplicationError({
				type: ErrorType.InvalidInput,
				message: 'This user is already a collaborator',
				inputErrors: {
					userHandler: 'This user is already a collaborator'
				},
				code: 'invalid_input'
			})
		}

		if (name === currentUser) {
			return new ApplicationError({
				type: ErrorType.InvalidInput,
				message: 'You cannot add yourself as a collaborator',
				inputErrors: {
					userHandler: 'You cannot add yourself as a collaborator'
				},
				code: 'invalid_input'
			})
		}

		return GuardInput.check(
			ForManagingProjects.AddCollaboratorPayload.partial(),
			{
				userHandler: name
			}
		)
	}

	static stableHash(value: string): number {
		let hash = 0
		for (let i = 0; i < value.length; i++) {
			const char = value.charCodeAt(i)
			// Simple 32-bit FNV-like or similar
			hash = (hash << 5) - hash + char
			hash |= 0 // Convert to 32-bit integer
		}
		return Math.abs(hash) // ensure it's not negative
	}

	static AssignColorToProject(project: ProjectV2): ProjectV2 {
		const colors = [
			{
				default: '#66d9ef',
				hover: '#52bcbf'
			},
			{
				default: '#0BCF7D',
				hover: '#09a664'
			},
			{
				default: '#9d6fff',
				hover: '#7f5ace'
			},
			{
				default: '#f35874',
				hover: '#d24e66'
			},
			{
				default: '#fd971f',
				hover: '#da841e'
			}
		]

		const hashValue = ProjectService.stableHash(project.name)
		const color = colors[hashValue % colors.length]
		project.color = color

		return project
	}
}
